import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { cn } from '../../lib/classNameHelpers';

export function InputGroup({ children }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      data-slot="control"
      className={clsx(
        'relative isolate block',
        '[&_input]:has-[[data-slot=icon]:first-child]:pl-10 [&_input]:has-[[data-slot=icon]:last-child]:pr-10 sm:[&_input]:has-[[data-slot=icon]:first-child]:pl-8 sm:[&_input]:has-[[data-slot=icon]:last-child]:pr-8',
        '[&>[data-slot=icon]]:pointer-events-none [&>[data-slot=icon]]:absolute [&>[data-slot=icon]]:top-3 [&>[data-slot=icon]]:z-10 [&>[data-slot=icon]]:size-5 sm:[&>[data-slot=icon]]:top-2.5 sm:[&>[data-slot=icon]]:size-4',
        '[&>[data-slot=icon]:first-child]:left-3 sm:[&>[data-slot=icon]:first-child]:left-2.5 [&>[data-slot=icon]:last-child]:right-3 sm:[&>[data-slot=icon]:last-child]:right-2.5',
        '[&>[data-slot=icon]]:text-zinc-500 dark:[&>[data-slot=icon]]:text-zinc-400'
      )}
    >
      {children}
    </span>
  );
}

const dateTypes = ['date', 'datetime-local', 'month', 'time', 'week'];
type DateType = (typeof dateTypes)[number];

type InputInnerProps = {
  type?: 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | DateType;
  className?: string;
} & Omit<Headless.InputProps, 'as' | 'className'>;

export const InputInner = forwardRef(function InputInner(
  { type, className, ...props }: InputInnerProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  return (
    <Headless.Input
      ref={ref}
      type={type}
      className={twMerge(
        // Date classes
        type &&
          dateTypes.includes(type) && [
            '[&::-webkit-datetime-edit-fields-wrapper]:p-0',
            '[&::-webkit-date-and-time-value]:min-h-[1.5em]',
            '[&::-webkit-datetime-edit]:inline-flex',
            '[&::-webkit-datetime-edit]:p-0',
            '[&::-webkit-datetime-edit-year-field]:p-0',
            '[&::-webkit-datetime-edit-month-field]:p-0',
            '[&::-webkit-datetime-edit-day-field]:p-0',
            '[&::-webkit-datetime-edit-hour-field]:p-0',
            '[&::-webkit-datetime-edit-minute-field]:p-0',
            '[&::-webkit-datetime-edit-second-field]:p-0',
            '[&::-webkit-datetime-edit-millisecond-field]:p-0',
            '[&::-webkit-datetime-edit-meridiem-field]:p-0',
          ],
        // Basic layout
        'block w-full min-w-0 appearance-none px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing[3])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
        // Typography
        'text-xs text-contentPrimary placeholder:text-contentSecondary dark:text-contentPrimary',
        // Background color & focus
        'bg-transparent focus:outline-none',
        // System icons
        'dark:[color-scheme:dark]',
        // Disabled
        'disabled:cursor-not-allowed',
        className
      )}
      {...props}
    />
  );
});

export type InputProps = {
  containerClassName?: string;
  children?: React.ReactNode;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  hasError?: boolean;
  containerOnClick?: (e: React.MouseEvent) => void;
} & InputInnerProps;

export const Input = forwardRef(function Input(
  {
    containerClassName,
    children,
    leftElement,
    rightElement,
    hasError,
    containerOnClick,
    ...props
  }: InputProps,
  // Ref is not currently forwarded if an input element is provided as children.
  // We may want to expect a ReactElement and use React.cloneElement to forward
  // the ref. Currently it's expected that instead the ref is passed manually to
  // the child input if provided.
  ref: React.ForwardedRef<HTMLInputElement>
) {
  return (
    <div
      data-slot="control"
      className={cn(
        // Basic layout
        'relative block w-full cursor-text px-3 py-1',
        'flex items-center rounded-md',
        // Default border & background
        'bg-backgroundTertiary transition-colors dark:bg-backgroundTertiary',
        // Border & outline handling
        'border border-backgroundTertiary hover:border-backgroundHoverOverlay',
        // Focus state
        'has-[input:focus]:border-borderInfo',
        'dark:has-[input:focus]:border-borderInfo',
        // Disabled state
        'data-[disabled=true]:hover:border-backgroundTertiary data-[disabled=true]:cursor-not-allowed',
        // Invalid state, set with hasError prop or data-invalid
        'dark:has-[[data-invalid]]:border-red-500',
        hasError &&
          'border-red-500 hover:border-red-500 has-[input:focus]:border-red-500 dark:has-[input:focus]:border-red-500',
        containerClassName
      )}
      onClick={containerOnClick}
      data-disabled={props.disabled}
    >
      {leftElement && <div className="mr-2 flex items-center">{leftElement}</div>}
      <div className="flex-1">{children ?? <InputInner ref={ref} {...props} />}</div>
      {rightElement && <div className="ml-2 flex items-center">{rightElement}</div>}
    </div>
  );
});
